import { Injectable, inject } from '@angular/core';
import { timer, Subscription } from 'rxjs';
import { CalAngularService } from '@cvx/cal-angular';
import { UserProfileStore } from '../../store/user-profile.store';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private calService = inject(CalAngularService);
  private userProfileStore = inject(UserProfileStore);
  private tokenExpirationTimer: Subscription | null = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    if (this.userProfileStore.isAuthenticated()) {
      const token = this.userProfileStore.token();
      this.scheduleTokenRenewal(token);
    }
  }

  /**
   * Runs authentication initialization once and reuses the same promise.
   */
  initializeAuth(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.login().catch((error) => {
        this.initPromise = null;
        throw error;
      });
    }

    return this.initPromise;
  }

  /**
   * Main login method: tries silent login, falls back to interactive.
   */
  async login(): Promise<void> {
    this.userProfileStore.StartLoading();
    try {
      // Try silent login
      const token = await this.getToken([environment.audience + '/user_impersonation'], true);
      if (token) {
        this.userProfileStore.SetToken(token);
        this.userProfileStore.SetUserProfile(this.extractProfileFromToken(token));
        this.userProfileStore.Authenticate(true);
        this.scheduleTokenRenewal(token);
        this.userProfileStore.EndLoading();
        return;
      }
    } catch {
      // Silent login failed, proceed to interactive
    }

    // Interactive login
    try {
      await this.performInteractiveSignIn();
      this.userProfileStore.EndLoading();
    } catch (err: any) {
      // interaction_in_progress means a redirect is already underway (page just
      // returned from AAD). Do nothing — MSAL will complete the flow automatically.
      if (err?.errorCode === 'interaction_in_progress') {
        this.userProfileStore.EndLoading();
        return;
      }
      this.handleLoginError(err);
      throw err;
    }
  }

  /**
   * Gets a token for the given scopes.
   */
  private async getToken(scopes: string[], silent: boolean = false): Promise<string> {
    return new Promise((resolve, reject) => {
      this.calService.getAADToken(scopes, silent).subscribe({
        next: (response) => {
          const token = typeof response === 'string' ? response : (response as any)?.accessToken || '';
          if (token) {
            this.userProfileStore.SetToken(token);
            resolve(token);
          } else {
            reject('No token received');
          }
        },
        error: (err) => {
          // Propagate interaction_in_progress as-is so login() can handle it gracefully
          reject(err);
        }
      });
    });
  }

  /**
   * Refreshes the API token.
   */
  async refreshApiToken(): Promise<string> {
    const token = await this.getToken([environment.audience + '/user_impersonation']);
    this.userProfileStore.SetToken(token);
    return token;
  }

  /**
   * Schedules token renewal before expiration.
   */
  private scheduleTokenRenewal(token: string | null): void {
    if (!token) return;
    const expirationTime = this.parseTokenExpiration(token);
    if (!expirationTime) return;

    const currentTime = new Date().getTime();
    const timeToExpire = expirationTime - currentTime - 5 * 60 * 1000; // 5 minutes before

    if (timeToExpire > 0) {
      if (this.tokenExpirationTimer) {
        this.tokenExpirationTimer.unsubscribe();
      }
      this.tokenExpirationTimer = timer(timeToExpire).subscribe(async () => {
        try {
          const newToken = await this.refreshApiToken();
          this.scheduleTokenRenewal(newToken);
        } catch {
          // Optionally handle logout or error
        }
      });
    }
  }

  /**
   * Decodes the token to get expiration time.
   */
  private parseTokenExpiration(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  /**
   * Handles login errors.
   */
  private handleLoginError(error: any): void {
    this.userProfileStore.EndLoadingWithError(error?.toString() || 'Login failed');
    this.userProfileStore.Authenticate(false);
  }

  /**
   * Performs interactive sign-in.
   */
  private async performInteractiveSignIn(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.calService.userInitiatedSignIn().subscribe({
        next: async (user: any) => {
          if (user?.isUser) {
            const token = await this.getToken([environment.audience + '/user_impersonation']);
            this.userProfileStore.SetUserProfile(this.extractProfileFromToken(token));
            this.userProfileStore.Authenticate(true);
            resolve();
          } else {
            reject('User not authenticated');
          }
        },
        error: (err: any) => reject(err)
      });
    });
  }

  /**
   * Extracts basic user profile (id, email, name) from a JWT access token's claims.
   * Avoids any Microsoft Graph dependency.
   */
  private extractProfileFromToken(token: string): { id: string; fullName: string; email: string } {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.oid || payload.sub || '',
        fullName: payload.name || '',
        email: payload.preferred_username || payload.upn || payload.unique_name || payload.email || ''
      };
    } catch {
      return { id: '', fullName: '', email: '' };
    }
  }
}